import React, { useState, useEffect } from 'react';
import { featureLimitsAPI } from '../../apis/featureLimits';
import { Card, Button, Alert, Badge, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { FaCopy, FaGift, FaUsers, FaShare, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SideNav from '../helpers/SideNavbar';

const ReferralSystem = () => {
  const [referralData, setReferralData] = useState({
    referralCode: '',
    referralUrl: '',
    totalReferrals: 0,
    successfulReferrals: 0,
    pendingReferrals: 0
  });
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const userId = localStorage.getItem('UserID');

  useEffect(() => {
    if (userId) {
      loadReferralData();
    }
  }, [userId]);

  const loadReferralData = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const [referralCodeResponse, statsResponse, creditsResponse] = await Promise.all([
        featureLimitsAPI.getReferralCode(userId),
        featureLimitsAPI.getReferralStats(userId),
        featureLimitsAPI.getUserCredits(userId)
      ]);

      setReferralData({
        referralCode: referralCodeResponse.referralCode,
        referralUrl: referralCodeResponse.referralUrl,
        ...statsResponse
      });

      setCredits(creditsResponse);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const copyReferralWithMessage = async (url) => {
    const message = `Hey! I'd love for you to join me on KnackVibes! 

Please click the below link to sign up and start creating amazing content:

${url}

You'll get access to powerful AI tools for content creation, and I'll earn some credits too! 🎉`;
    
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success('Link & Message copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const openGmail = (url) => {
    const subject = 'Join me on KnackVibes!';
    
    // Create message with proper line breaks
    const message = `Hey! I'd love for you to join me on KnackVibes!

Please click the below link to sign up and start creating amazing content:

${url}

You'll get access to powerful AI tools for content creation, and I'll earn some credits too! 🎉`;
    
    // Encode the message
    const body = encodeURIComponent(message);
    
    // Create Gmail compose URL with rich text formatting enabled
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&ui=2&tf=1&to=&su=${encodeURIComponent(subject)}&body=${body}`;
    
    // Open Gmail in a new tab
    window.open(gmailUrl, '_blank');
    
    toast.success('Gmail opened with rich text formatting!');
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join KnackVibes with my referral!',
        text: 'Get started with KnackVibes and unlock amazing features!',
        url: referralData.referralUrl
      });
    } else {
      copyToClipboard(referralData.referralUrl);
    }
  };

  const getTotalCredits = () => {
    return credits.reduce((total, credit) => total + (credit.available_credits || 0), 0);
  };

  const getCreditsNeededForRedemption = () => {
    const total = getTotalCredits();
    const needed = 10 - (total % 10);
    return needed === 10 ? 0 : needed;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <SideNav>
      <div className="container-fluid p-4">
      <div className="row mb-4">
        <div className="col-12">
          <Card className="shadow-sm">
            <Card.Header className="bg-success text-white">
              <h4 className="mb-0">
                <FaGift className="me-2" />
                Referral Program
              </h4>
              <small>Earn credits by referring friends</small>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Your Referral Code</h5>
                  <InputGroup className="mb-3">
                    <Form.Control
                      type="text"
                      value={referralData.referralCode}
                      readOnly
                      className="font-monospace"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(referralData.referralCode)}
                    >
                      <FaCopy />
                    </Button>
                  </InputGroup>
                  
                  <h5>Your Referral Link</h5>
                  <InputGroup className="mb-3">
                    <Form.Control
                      type="text"
                      value={referralData.referralUrl}
                      readOnly
                      className="font-monospace"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(referralData.referralUrl)}
                    >
                      <FaCopy />
                    </Button>
                  </InputGroup>
                  
                  {/* Explanatory text */}
                  <div className="mb-3">
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#6c757d', 
                      fontStyle: 'italic',
                      textAlign: 'center',
                      margin: '0.5rem 0'
                    }}>
                      Please click the below link to share with your friends and earn credits!
                    </p>
                  </div>
                  
                  {/* Enhanced copy button with message */}
                  <div className="text-center mb-3">
                    <Button
                      variant="primary"
                      onClick={() => copyReferralWithMessage(referralData.referralUrl)}
                      style={{ 
                        backgroundColor: '#2e3192',
                        borderColor: '#2e3192', 
                        color: 'white',
                        borderRadius: '0.375rem',
                        fontWeight: '600',
                        padding: '12px 20px',
                        fontSize: '16px',
                        marginRight: '10px',
                        marginBottom: '10px'
                      }}
                    >
                      <FaCopy style={{ marginRight: '8px' }} />
                      Copy Message (Recommended)
                    </Button>
                    
                    <Button
                      variant="outline-success"
                      onClick={() => openGmail(referralData.referralUrl)}
                      style={{ 
                        borderColor: '#28a745', 
                        color: '#28a745',
                        borderRadius: '0.375rem',
                        fontWeight: '500',
                        padding: '12px 20px',
                        fontSize: '16px',
                        marginBottom: '10px'
                      }}
                    >
                      📧 Open Gmail
                    </Button>
                  </div>

                  <Button
                    variant="success"
                    size="lg"
                    onClick={shareReferral}
                    className="w-100"
                  >
                    <FaShare className="me-2" />
                    Share Referral Link
                  </Button>
                </Col>

                <Col md={6}>
                  <h5>Referral Statistics</h5>
                  <div className="row text-center">
                    <div className="col-4">
                      <div className="border rounded p-3">
                        <div className="fs-3 text-primary">{referralData.totalReferrals}</div>
                        <small className="text-muted">Total Referrals</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border rounded p-3">
                        <div className="fs-3 text-success">{referralData.successfulReferrals}</div>
                        <small className="text-muted">Successful</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="border rounded p-3">
                        <div className="fs-3 text-warning">{referralData.pendingReferrals}</div>
                        <small className="text-muted">Pending</small>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <FaGift className="me-2" />
                Your Credits
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <div className="fs-1 text-success">{getTotalCredits()}</div>
                <small className="text-muted">Total Credits</small>
              </div>

              {credits.length > 0 && (
                <div className="mb-3">
                  <h6>Credit Breakdown:</h6>
                  {credits.map((credit, index) => (
                    <div key={index} className="d-flex justify-content-between">
                      <span>{credit.credit_type}</span>
                      <Badge bg="info">{credit.available_credits}</Badge>
                    </div>
                  ))}
                </div>
              )}

              {getTotalCredits() >= 10 && (
                <Alert variant="success" className="text-center">
                  <FaCheckCircle className="me-2" />
                  <strong>Congratulations!</strong><br />
                  You have enough credits for automatic redemption!
                </Alert>
              )}

              {getTotalCredits() < 10 && (
                <Alert variant="info" className="text-center">
                  <strong>Keep Going!</strong><br />
                  You need {getCreditsNeededForRedemption()} more credits to reach 10 for automatic redemption.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-6">
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <FaUsers className="me-2" />
                How It Works
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <h6>Earn Credits:</h6>
                <ul className="list-unstyled">
                  <li>✅ <strong>5 credits</strong> for each successful referral</li>
                  <li>✅ <strong>Bonus credits</strong> for special promotions</li>
                </ul>
              </div>

              <div className="mb-3">
                <h6>Redeem Credits:</h6>
                <ul className="list-unstyled">
                  <li>✅ <strong>10 credits</strong> = 30 days subscription extension</li>
                  <li>✅ <strong>Automatic redemption</strong> when you reach 10+ credits</li>
                </ul>
              </div>

              <div className="mb-3">
                <h6>Referral Process:</h6>
                <ol className="small">
                  <li>Share your referral link with friends</li>
                  <li>Friend signs up using your link</li>
                  <li>You earn 5 credits automatically</li>
                  <li>Credits are redeemed when you reach 10+</li>
                </ol>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      </div>
    </SideNav>
  );
};

export default ReferralSystem;
